import { errorForm, navigation, task } from '@origin-digital/reporting-client';
import { AxiosPromise } from 'axios';
import * as React from 'react';
import { ChangeEvent, FormEvent } from 'react';
import { AccountResponse } from 'ts/services/find-account/AccountResponse';
import { FindEmailView } from './FindEmailView';
import { Content } from './Interfaces';

interface FindEmailPageProps {
  history: any;
  findAccountAction: (
    lastname: string,
    dob: string,
    postcode: string,
  ) => AxiosPromise<AccountResponse>;
  nextAction: (email: string) => void;
  conflictAccountAction: (lastName: string, dob: string, postcode: string) => void;
  errorPageAction: (statusCode: number) => void;
  content: Content;
}

class FindEmailPage extends React.PureComponent<FindEmailPageProps> {
  public state = {
    anyTextFieldChanged: false,
    lastName: '',
    lastNameError: '',
    dob: '',
    dobError: '',
    postcode: '',
    postcodeError: '',
    spinner: false,
    alertBoxError: '',
  };

  private DATE_REGEX = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/;

  private POSTCODE_REGEX = /^\d{4}$/;

  public componentDidMount() {
    navigation('screen', {
      currentUri: '',
      friendlyUri: this.props.content.analytics.navigation.friendlyUri,
    });
  }

  public render() {
    return (
      <FindEmailView
        history={this.props.history}
        copy={this.props.content.copy}
        onDOBChange={this.onDOBChange}
        onDOBBlur={this.onDOBBlur}
        dobError={this.state.dobError}
        lastNameError={this.state.lastNameError}
        onLastNameBlur={this.onLastNameBlur}
        onLastNameChange={this.onLastNameChange}
        onPostcodeChange={this.onPostcodeChange}
        onPostcodeBlur={this.onPostcodeBlur}
        postcodeError={this.state.postcodeError}
        onSubmit={this.onSubmit}
        spinner={this.state.spinner}
        alertBoxError={this.state.alertBoxError}
      />
    );
  }

  private onSubmit = async (e: FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    let containsValidationError = false;
    // validate all fields & show inline error if any are invalid
    if (!this.validateLastName(this.state.lastName)) {
      containsValidationError = true;
    }
    if (!this.validateDOB(this.state.dob)) {
      containsValidationError = true;
    }
    if (!this.validatePostcode(this.state.postcode)) {
      containsValidationError = true;
    }
    if (containsValidationError) {
      return;
    }

    this.setState({ spinner: true, alertBoxError: '' });
    try {
      const accountsResponse = await this.props.findAccountAction(
        this.state.lastName,
        this.state.dob,
        this.state.postcode,
      );
      this.props.nextAction(accountsResponse.data.email);
    } catch (error) {
      this.setState({ spinner: false });
      const { response = {} } = error || {};
      this.handleSearchErrors(response.status);
    }
  };

  private handleSearchErrors = (statusCode: number) => {
    switch (statusCode) {
      case 404:
        this.handleNoAccounts();
        break;
      case 409:
        this.handleConflictAccount();
        break;
      default:
        this.props.errorPageAction(statusCode);
    }
  };

  private handleConflictAccount = () => {
    this.props.conflictAccountAction(this.state.lastName, this.state.dob, this.state.postcode);
  };

  private handleNoAccounts = () => {
    const { formName, errorMessage, errorCode } = this.props.content.analytics.errorForm;
    errorForm({
      formName,
      errorMessage,
      errorCode,
    });
    this.setState({
      spinner: false,
      alertBoxError: this.props.content.copy.alertBoxText,
    });
  };

  private onLastNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.textFieldChanged();
    this.setState({
      lastName: e.target.value,
      lastNameError: '',
    });
  };

  private onDOBChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.textFieldChanged();
    this.setState({
      dob: e.target.value,
      dobError: '',
    });
  };

  private onPostcodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.textFieldChanged();
    this.setState({
      postcode: e.target.value,
      postcodeError: '',
    });
  };

  private onLastNameBlur = (e: ChangeEvent<HTMLInputElement>) => {
    this.validateLastName(e.target.value);
  };

  private onDOBBlur = (e: ChangeEvent<HTMLInputElement>): void => {
    this.validateDOB(e.target.value);
  };

  private onPostcodeBlur = (e: ChangeEvent<HTMLInputElement>) => {
    this.validatePostcode(e.target.value);
  };

  private validateDOB = (dob: string): boolean => {
    if (!this.DATE_REGEX.test(dob)) {
      this.setState({
        dobError: 'Hmm, is that DOB correct? It should be DD/MM/YYYY',
      });
      return false;
    }
    return true;
  };

  private validatePostcode = (postcode: string): boolean => {
    if (!this.POSTCODE_REGEX.test(postcode)) {
      this.setState({
        postcodeError: 'Hmm, that postcode should just be four numbers',
      });
      return false;
    }
    return true;
  };

  private validateLastName = (lastName: string): boolean => {
    if (!lastName) {
      this.setState({
        lastNameError: 'Oops, please enter your last name',
      });
      return false;
    }
    return true;
  };

  private textFieldChanged = () => {
    if (!this.state.anyTextFieldChanged) {
      task.base(
        this.props.content.analytics.taskType as any,
        {
          action: 'start',
        } as any,
      );
    }
    this.setState({
      anyTextFieldChanged: true,
    });
  };
}

export { FindEmailPage };
